import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export interface MoodOption {
  id: string;
  emoji?: string;
  label?: string;
}

export interface MoodControlProps {
  options: ReadonlyArray<MoodOption>;
  value: string | null;
  onChange: (next: string) => void;
  readOnly?: boolean;
  size?: "md" | "lg";
}

/// Single-select emoji mood picker for the self check-in's `mood`
/// question type. Large tap targets, label under the emoji, accessible
/// via the visible label (the emoji is decorative). Native radio inputs
/// give real roving arrow-key selection (mirrors the audience picker
/// pattern); the visible button shape is a styled `<label>`.
export function MoodControl({
  options,
  value,
  onChange,
  readOnly,
  size = "md",
}: MoodControlProps): ReactNode {
  return (
    <div role="radiogroup" aria-label="Mood" className="flex flex-wrap gap-2">
      {options.map((o) => {
        const selected = value === o.id;
        return (
          <label
            key={o.id}
            className={cn(
              "flex cursor-pointer flex-col items-center rounded-lg",
              "ring-1 ring-inset transition-[transform,background-color,box-shadow] duration-200 ease-out",
              "focus-within:ring-2 focus-within:ring-ring active:scale-95 motion-reduce:transform-none",
              size === "lg" ? "min-w-20 gap-1.5 px-4 py-3.5" : "min-w-16 gap-1 px-3 py-2.5",
              selected
                ? "bg-brand-soft-bg text-text-brand-strong ring-2 ring-primary scale-[1.03] ease-spring"
                : "bg-surface-elevate text-text-secondary ring-border hover:bg-surface-muted",
              readOnly && "cursor-default",
              readOnly && !selected && "opacity-50",
              readOnly && "active:scale-100",
            )}
          >
            <input
              type="radio"
              name="mood"
              value={o.id}
              checked={selected}
              disabled={readOnly}
              onChange={() => onChange(o.id)}
              className="sr-only"
            />
            {o.emoji ? (
              <span
                aria-hidden="true"
                className={cn("leading-none", size === "lg" ? "text-3xl" : "text-2xl")}
              >
                {o.emoji}
              </span>
            ) : null}
            <span className={cn("font-medium", size === "lg" ? "text-sm" : "text-xs")}>
              {o.label ?? o.id}
            </span>
          </label>
        );
      })}
    </div>
  );
}
